import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getNewsBySlug } from "../../API/News/newsService";
import { CalendarClock } from "lucide-react";
import { Button } from "../../components/ui/button";

const NewsSinglePage = () => {
  const uploadUrl = process.env.REACT_APP_FILE_UPLOAD;
  const { id } = useParams();
  const [newsData, setNewsData] = useState<Record<string, any> | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (id) {
        const data = await getNewsBySlug(id);

        setNewsData(data);
      }
    };

    if (id) {
      fetchData();
    }
  }, [id]);

  const extractPdfLink = (html: string) => {
    const urlMatch = html.match(/href=["']([^"']+\.pdf)["']/i);
    if (urlMatch) return urlMatch[1];

    const base64Match = html.match(
      /href=["'](data:application\/pdf;base64,[^"']+)["']/i
    );
    return base64Match ? base64Match[1] : null;
  };

  const pdfUrl = newsData?.news?.description
    ? extractPdfLink(newsData?.news?.description || "")
    : null;

  const removePdfLinks = (html: string) => {
    return html.replace(
      /<a[^>]+href=["'](data:application\/pdf;base64,[^"']+)["'][^>]*>.*?<\/a>/gi,
      ""
    );
  };

  return (
    <div className="mt-10">
      <div className="container mx-auto mt-20 mb-20 md:px-40 sm:px-40">
        <div className="mt-5">
          <h1 className="text-2xl font-bold text-black">
            {newsData?.news?.title || "Titre"}
          </h1>
          <div className="flex mt-2 space-x-3">
            <CalendarClock className="text-primary" />
            <div className="text-black">
              {newsData?.news?.date
                ? new Date(newsData.news.date).toLocaleDateString("fr-FR", {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })
                : new Date().toLocaleDateString("fr-FR")}
            </div>
          </div>

          <hr className="w-full h-1 mt-2 bg-primary" />
        </div>

        {/* News Image */}
        {newsData?.news?.image && (
          <div className="mt-5">
            <img src={`${uploadUrl}/news/${newsData.news.image}`} alt="" />
          </div>
        )}

        {/* News Description */}
        <div className="mt-10">
          {newsData?.news?.description && (
            <div
              dangerouslySetInnerHTML={{
                __html: removePdfLinks(newsData.news.description),
              }}
            />
          )}
        </div>

        {pdfUrl && (
          <div className="mt-4">
            <a
              href={pdfUrl}
              download="document.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 "
            >
              📄 Télécharger le document PDF
            </a>
          </div>
        )}
      </div>

      {/* Similar News */}
      <div className="bg-bg_aquavesc">
        <div className="p-24 lg:mr-32 lg:ml-32">
          <h1 className="text-2xl font-bold text-black">À lire aussi</h1>
          <div className="grid grid-cols-1 gap-4 justify-items-center md:grid-cols-2 md:p-6">
            {newsData?.similar?.length > 0 &&
              newsData?.similar.map((card: any) => (
                <div key={card.id} className="w-full group">
                  <div className="relative flex flex-col h-full overflow-hidden transition-transform duration-300 ease-in-out bg-white rounded-lg shadow-lg group-hover:bg-primary group-hover:shadow-xl hover:-translate-y-2">
                    {/* Image */}
                    <img
                      alt={card.title}
                      className="object-cover w-full h-60 md:h-96"
                      src={`${uploadUrl}/news/${card.image}`}
                    />

                    {/* Content Wrapper */}
                    <div className="flex flex-col flex-grow bg-white dark:bg-gray-950 group-hover:bg-primary">
                      <h3 className="p-4 text-xl font-bold group-hover:text-white">
                        {card.title}
                      </h3>
                      <p className="pl-4 group-hover:text-white">
                        Publié le{" "}
                        {new Date(card.date).toLocaleDateString("fr-FR", {
                          month: "long",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </p>

                      {/* Button at the Bottom */}
                      <div className="p-4 mt-auto">
                        <Link to={`/actualités/${card.slug}`}>
                          <Button className="w-full group-hover:bg-secondary group-hover:text-black">
                            Voir plus
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewsSinglePage;
